import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { selectIsIterableGoalsFlagOn } from 'core/userContext';
import { BoldText } from 'modules/campaigns/editor/styles';
import { pluralizeWord } from 'modules/campaigns/lib/utils';
import { AttributionMode } from 'modules/domains/campaign';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { useCampaignContext, useEditorContext } from '../../../context';
import { shouldExcludeConversionsStep } from '../../CampaignCard/utils';
import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import {
  CommaSeparatedList,
  LightText,
  SectionHeader,
  SectionWrapper,
  StyledNewTabEventLink,
} from '../Sections.styled';
import { EditableConversion } from './EditableConversion';
import { CodeBlock } from './EditableConversion.styles';
import { Experiments } from './Experiments';

export const CONVERSION_SECTION_TEST_ID = 'conversion-section';
export const ITERABLE_GOAL_LINK_TEST_ID = 'iterable-goal-link';

export const ConversionSection = () => {
  const { campaign } = useCampaignContext();
  const { campaignSteps } = useEditorContext();
  const query = useQueryParams();
  const showConversionsForEmbedded = query.get('__embeddedConversionEnabled');
  const isSelectIsIterableGoalsFlagOn = useSelector(selectIsIterableGoalsFlagOn);
  const isWhatsAppGAEnabled = useFeatureFlag('WhatsAppMessageChannelGA');
  const { useConversions, events, mode, periodHours } = useWatch({ name: 'conversions' });
  const isEmbeddedCampaign = campaign.messageMedium === EmbeddedMessage;
  const isWhatsAppCampaign = campaign.messageMedium === WhatsApp;

  const isEditable =
    campaignSteps?.Conversions?.isEditable || campaignSteps?.ConversionAndExperiment?.isEditable;

  const attributionWithPeriodHoursText = (
    <span>
      Attribute any conversion events received within{' '}
      <BoldText>
        {periodHours} {pluralizeWord('hour', periodHours)}
      </BoldText>{' '}
      of sending the message
    </span>
  );

  const attributionModeDisplayMapper = {
    [AttributionMode.LastMessaged]: attributionWithPeriodHoursText,
    [AttributionMode.AttributionPeriod]: attributionWithPeriodHoursText,
    [AttributionMode.ExplicitlySet]: (
      <span>
        Only conversion events with explicitly set <CodeBlock>campaignId</CodeBlock> field
      </span>
    ),
  };

  // Temporarily add conversion section for Embedded Campaigns enabled by query param to allow
  // for product testing around Conversion enablement for Embedded
  if (isEmbeddedCampaign && !showConversionsForEmbedded) {
    return <></>;
  }

  // TODO: Remove check after WhatsApp GA release
  if (shouldExcludeConversionsStep(campaign.messageMedium, isWhatsAppGAEnabled)) {
    return <></>;
  }

  return (
    <SectionWrapper data-test={CONVERSION_SECTION_TEST_ID}>
      <SectionHeader>Conversions</SectionHeader>

      <CampaignFormField labelText="Conversion events">
        {isEditable ? (
          <EditableConversion />
        ) : (
          <ReadOnlyField name="conversionEvents">
            <CommaSeparatedList>
              {useConversions ? (
                events?.map((e: string, i: number) => {
                  const row = isSelectIsIterableGoalsFlagOn ? (
                    <StyledNewTabEventLink
                      data-test={ITERABLE_GOAL_LINK_TEST_ID}
                      href={`/analytics/goalPerformance?goalName=${encodeURIComponent(
                        e,
                      )}&referrer=campaign-editor`}
                    >
                      {e}
                    </StyledNewTabEventLink>
                  ) : (
                    <>{e}</>
                  );
                  return <li key={e + i}>{row}</li>;
                })
              ) : (
                <LightText>Not set</LightText>
              )}
            </CommaSeparatedList>
          </ReadOnlyField>
        )}
      </CampaignFormField>

      {!isEditable && (
        <CampaignFormField labelText="Attribution mode">
          <ReadOnlyField name="attributionMode">
            {useConversions ? (
              attributionModeDisplayMapper[mode as AttributionMode]
            ) : (
              <LightText>Not set</LightText>
            )}
          </ReadOnlyField>
        </CampaignFormField>
      )}

      {!(isEmbeddedCampaign || isWhatsAppCampaign) && <Experiments />}
    </SectionWrapper>
  );
};
